import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';

export interface AiTokensByDay {
    date: string;
    tokens: number;
    queries: number;
}

export interface AiTopUser {
    userId: number;
    userName: string | null;
    email: string;
    tokens: number;
    queries: number;
}

export interface AiQueryTypeBreakdown {
    type: string;
    count: number;
    percentage: number;
}

export interface AiUsageStats {
    totalTokensThisMonth: number;
    totalTokensAllTime: number;
    estimatedCostThisMonth: number;   // USD
    estimatedCostAllTime: number;
    totalQueriesThisMonth: number;
    totalQueriesAllTime?: number;
    uniqueUsersThisMonth: number;
    uniqueUsersAllTime?: number;
    tokensByDay: AiTokensByDay[];
    topUsers: AiTopUser[];
    queryTypeBreakdown: AiQueryTypeBreakdown[];
}

export type AiRoleKey = 'company_learner' | 'company_author';

export interface AiRoleLimits {
    roleKey: AiRoleKey;
    dailyTokenLimit: number | null;
    weeklyTokenLimit: number | null;
    monthlyTokenLimit: number | null;
    isEnabled: boolean;
}

export interface AiCompanySettings {
    isAiEnabled: boolean;
    limits: AiRoleLimits[];
}

export interface AiApiError {
    message: string;
    errors?: Record<string, string[]>;
}

/**
 * Servicio API para el módulo de administración de IA.
 * Hace llamadas HTTP a los endpoints del backend.
 */
@Injectable({
    providedIn: 'root'
})
export class AiAdminApiService {
    private baseUrl: string;

    constructor(private http: HttpClient) {
        const apiUrl = environment.envVar.API_URL.endsWith('/')
            ? environment.envVar.API_URL.slice(0, -1)
            : environment.envVar.API_URL;
        this.baseUrl = `${apiUrl}/company`;
    }

    private url(companyId: string): string {
        return `${this.baseUrl}/${companyId}/ai`;
    }

    /**
     * Obtiene las estadísticas de uso de IA para la empresa.
     * GET /company/{companyId}/ai/usage
     */
    getUsageStats(companyId: string): Observable<AiUsageStats> {
        return this.http.get<AiUsageStats>(`${this.url(companyId)}/usage`);
    }

    /**
     * Obtiene la configuración de IA de la empresa (límites por rol, etc.).
     * GET /company/{companyId}/ai/settings
     */
    getSettings(companyId: string): Observable<AiCompanySettings> {
        return this.http.get<AiCompanySettings>(`${this.url(companyId)}/settings`);
    }

    /**
     * Actualiza la configuración de IA de la empresa.
     * PUT /company/{companyId}/ai/settings
     */
    updateSettings(companyId: string, data: AiCompanySettings): Observable<AiCompanySettings> {
        return this.http.put<AiCompanySettings>(`${this.url(companyId)}/settings`, data);
    }
}
